import { confirmApp } from '../common/appDialog';
import React from 'react';
import { industryConfig, ICONS } from './constants';
import type { CustomIndustry, Page } from '@types';

interface IndustrySelectionProps {
    onSelectIndustry: (industry: string) => void;
    customIndustries: CustomIndustry[];
    onDeleteIndustry: (industryName: string) => void;
    onBack: () => void;
    onNavigate: (page: Page) => void;
}

interface IndustryCardProps {
    name: string;
    details: {
        description: string;
        icon: React.ReactNode;
        imageUrl: string;
    };
    onClick: () => void;
}

const IndustryCard: React.FC<IndustryCardProps> = ({ name, details, onClick }) => (
    <div
        onClick={onClick}
        className="group relative cursor-pointer bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center overflow-hidden"
    >
        <div className="relative z-10 w-full flex-grow flex flex-col items-center">
            <div className="p-4 rounded-full bg-slate-100 text-blue-600 group-hover:bg-blue-100 transition-colors">
                {details.icon}
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">{name}</h3>
            <p className="mt-2 text-sm text-slate-600 flex-grow">{details.description}</p>
            <div className="text-right mt-5 h-5 w-full">
                <span className="text-sm font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open Dashboard &rarr;
                </span>
            </div>
        </div>
    </div>
);

interface CustomIndustryCardProps {
    name: string;
    onSelect: () => void;
    onDelete: () => void;
}

const CustomIndustryCard: React.FC<CustomIndustryCardProps> = ({ name, onSelect, onDelete }) => (
    <div
        className="group relative bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center overflow-hidden"
    >
        <button
            onClick={(e) => {
                e.stopPropagation(); // Prevent the card's onClick from firing
                onDelete();
            }}
            className="absolute top-2 right-2 p-1.5 bg-slate-100 text-slate-500 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-100 hover:text-red-600 transition-all z-10"
            aria-label={`Delete ${name}`}
        >
            {React.cloneElement(ICONS.trash as any, { className: 'h-4 w-4' })}
        </button>

        <div onClick={onSelect} className="cursor-pointer w-full h-full flex flex-col items-center relative z-10">
             <div className="p-4 rounded-full bg-slate-100 text-indigo-600 group-hover:bg-indigo-100 transition-colors">
                {React.cloneElement(ICONS.folder as any, { className: 'h-10 w-10' })}
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">{name}</h3>
            <p className="mt-2 text-sm text-slate-600 flex-grow">Custom collection of intelligence suites.</p>
            <div className="text-right mt-5 h-5 w-full">
                <span className="text-sm font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open Dashboard &rarr;
                </span>
            </div>
        </div>
    </div>
);

const CreateIndustryCard: React.FC<{ onClick: () => void; }> = ({ onClick }) => {
    // FIX: Removed the invalid reference to SUITE_DETAILS.create_industry.
    // Replaced with a valid icon and hardcoded text to prevent the application from crashing.
    return (
         <div
            onClick={onClick}
            className="group cursor-pointer bg-white p-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 flex flex-col items-center justify-center text-center"
        >
            <div className="p-4 rounded-full bg-slate-100 text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                {React.cloneElement(ICONS.folder_plus as any, { className: 'h-10 w-10' })}
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Create Custom Industry</h3>
            <p className="mt-2 text-sm text-slate-600 flex-grow">Build a tailored dashboard by selecting the exact suites you need.</p>
        </div>
    );
};


const IndustrySelection: React.FC<IndustrySelectionProps> = ({ onSelectIndustry, customIndustries, onDeleteIndustry, onBack, onNavigate }) => {

    const handleDelete = async (industryName: string) => {
        if (await confirmApp(`Are you sure you want to permanently delete the '${industryName}' industry profile? This action cannot be undone.`)) {
            onDeleteIndustry(industryName);
        }
    };

    return (
        <div className="animate-fade-in">
             <button
                onClick={onBack}
                className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 mb-6 transition-colors duration-200"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Knowledge Hub
            </button>
            <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-800 mb-2">Industry-Specific Dashboards</h2>
                <p className="text-slate-500 mb-8 max-w-2xl mx-auto">Select a pre-configured industry dashboard or create your own custom-built suite.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {Object.keys(industryConfig).map((name) => {
                    const details = industryConfig[name];
                    return (
                        <IndustryCard 
                            key={name}
                            name={name}
                            details={details}
                            onClick={() => onSelectIndustry(name)} 
                        />
                    );
                })}
                 {customIndustries.map(industry => (
                    <CustomIndustryCard 
                        key={industry.name}
                        name={industry.name}
                        onSelect={() => onSelectIndustry(industry.name)}
                        onDelete={() => handleDelete(industry.name)}
                    />
                ))}
                <CreateIndustryCard onClick={() => onNavigate('customIndustryBuilder')} />
            </div>
        </div>
    );
};

export default IndustrySelection;

